import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  isAnalyticsAllowed,
  isGpcEnabled,
  setConsent,
} from "@/lib/cookie-consent";
import { disableAnalytics, initializeAnalytics } from "@/lib/analytics";

interface CookiePreferencesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CookiePreferencesSheet = ({
  open,
  onOpenChange,
}: CookiePreferencesSheetProps) => {
  const gpcEnabled = isGpcEnabled();
  const [analytics, setAnalytics] = useState(() => isAnalyticsAllowed());

  // Sync the toggle to the current stored preference each time the sheet opens.
  useEffect(() => {
    if (open) {
      setAnalytics(isAnalyticsAllowed());
    }
  }, [open]);

  const handleSave = () => {
    setConsent({ analytics });
    if (analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="text-left">
          <SheetTitle>Cookie Preferences</SheetTitle>
          <SheetDescription>
            Manage how we use cookies on this site. Necessary cookies are always
            on. You can opt out of analytics cookies at any time.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 py-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium">Necessary Cookies</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Required for the site to function. These are always active and
                cannot be disabled.
              </p>
            </div>
            <Switch
              checked
              disabled
              aria-label="Necessary cookies (always on)"
            />
          </div>

          <Separator />

          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium">Analytics Cookies</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Help us understand how visitors use the site so we can improve
                it. We do not sell this information.
              </p>
              {gpcEnabled && (
                <p className="text-xs text-muted-foreground mt-1">
                  Your browser's Global Privacy Control signal is enabled, so
                  analytics is turned off and cannot be enabled here.
                </p>
              )}
            </div>
            <Switch
              checked={gpcEnabled ? false : analytics}
              onCheckedChange={setAnalytics}
              disabled={gpcEnabled}
              aria-label="Analytics cookies"
            />
          </div>
        </div>

        <SheetFooter>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            Save Preferences
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
